import { CharacterCount, Paragraph } from '@/extensions';
import { ExtensionKit } from '@/extensions/simple-extension-kit';
import { Box } from '@chakra-ui/react';
import { Editor } from '@tiptap/core';
import { AnyExtension, EditorContent, useEditor, UseEditorOptions } from '@tiptap/react';
import { useEffect, useRef } from 'react';
import { Control, useController } from 'react-hook-form';
import { LinkMenu } from '../menus';
import { SimpleTextMenu } from './SimpleTextMenu';

interface SimpleEditorProps {
  name: string;
  control: Control;
  noCSS?: boolean;
  options?: UseEditorOptions;
  skipExtensionConfigs?: boolean;
  maxLength?: number;
}

const extensions = [
  ...ExtensionKit(),
].filter((e): e is AnyExtension => e !== undefined);

export default function SimpleEditor({ name, control, noCSS, options = {}, skipExtensionConfigs, maxLength }: SimpleEditorProps) {
  if (maxLength)
    extensions.push(CharacterCount.configure({ limit: maxLength }),)
  const menuContainerRef = useRef(null);

  const extensionsConfigs = [
    Paragraph.configure({
      HTMLAttributes: {
        style: 'color: #525753; font-size: 16px; font-style: normal; font-weight: 400; line-height: 20px;'
      }
    })
  ];

  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  const editor = useEditor(
    {
      immediatelyRender: true,
      shouldRerenderOnTransaction: false,
      autofocus: false,
      onCreate: ctx => {
        if (value) {
          ctx.editor.commands.setContent(value, true)
        }
        //ctx.editor.commands.focus('start', { scrollIntoView: true })
      },
      extensions: skipExtensionConfigs ? extensions : [...extensions, ...extensionsConfigs],
      editorProps: {
        transformPastedHTML(html) {
          const elementHtml = document.createElement('br')
          elementHtml.innerHTML = html
          
          return elementHtml.textContent || elementHtml.innerText || '';
          //return ctx.editor.commands.unsetAllMarks(html)
          //return html.replace(/\xA0/g, " ");
        },
        attributes: {
          autocomplete: 'off',
          autocorrect: 'off',
          autocapitalize: 'off',
          class: 'min-h-full',
        },
      },
      ...options
    },
    [],
  );

  useEffect(() => {
    if (onChange) {
      const handleUpdate = ({ editor }: { editor: Editor}) => {
        onChange(!editor.isEmpty ? editor.getHTML() : null);
      };
  
      editor.on('update', handleUpdate);
      return () => {
        editor.off('update', handleUpdate);
      }
    }
    
  }, [editor, onChange]);

  if (!editor) {
    return null
  }

  const style = noCSS ? {} : {
    borderRadius: '12px',
    borderWidth: '1px',
    borderColor: 'border.input.outline',
  }

  return (
    <Box
      overflow="hidden"
      ref={menuContainerRef}
      {...style}
    >
      <EditorContent 
        editor={editor} 
        className={`flex-1 overflow-y-auto simple-editor min-h-[148px] ${noCSS ? 'transparent' : ''}`}
      />
      <SimpleTextMenu editor={editor} />
      <LinkMenu editor={editor} appendTo={menuContainerRef} />
    </Box>
  )
}
